import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Card } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import MerchantTable from "./components/MerchantTable";
import { merchantColumns } from "./components/columns";
import useApi from "../../components/Utils/api";

// The axios layer rejects with a normalised { status, message, data } object,
// so read those fields rather than axios's raw `error.response`.
const describeError = (err) => {
  if (!err) return "Something went wrong. Please try again.";
  if (err.status === 0) {
    return "Could not reach the middleware API. Check that the service is running and reachable.";
  }
  return err.data?.message || err.data?.error || err.message || "Something went wrong.";
};

export default function MerchantsList() {
  const api = useApi();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchMerchants = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const result = await api.get("merchant/allMerchant");
      const list = Array.isArray(result?.data?.merchants) ? result.data.merchants : [];
      setRows(list);
      setTotal(Number.isInteger(result?.data?.total) ? result.data.total : list.length);
    } catch (e) {
      setError(describeError(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchMerchants();
  }, [fetchMerchants]);

  const handleView = (merchant) => {
    navigate(`/dashboard/${encodeURIComponent(merchant.internalMerchantId)}`, {
      state: { merchant, total },
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {error ? (
          <Card>
            <MDBox px={3} py={6} display="flex" flexDirection="column" alignItems="center" gap={2}>
              <Alert severity="error" sx={{ maxWidth: 560 }}>
                {error}
              </Alert>
              <MDTypography
                variant="button"
                color="text"
                sx={{ textTransform: "none", textAlign: "center" }}
              >
                The merchant list could not be loaded.
              </MDTypography>
              <MDButton
                variant="gradient"
                color="info"
                onClick={fetchMerchants}
                startIcon={<RefreshIcon />}
              >
                Try again
              </MDButton>
            </MDBox>
          </Card>
        ) : (
          <MerchantTable
            rows={rows}
            columns={merchantColumns}
            onView={handleView}
            loading={loading}
            total={total}
          />
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
